import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Plus, DollarSign, Clock, AlertTriangle, User, Search, Users, History, Target, UserX } from 'lucide-react';
import { devLog } from '@/utils/logger';
import { useCompanyEmployees } from '@/hooks/leave/useCompanyEmployees';
import { useCompensationRules, useCompensationAssignments } from '@/hooks/compensation';
import { toast } from '@/hooks/use-toast';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import { CompensationRuleDialog } from './CompensationRuleDialog';
import { RuleAssignmentDialog } from './RuleAssignmentDialog';
import { BulkAssignmentDialog } from './BulkAssignmentDialog';
import { ConflictDetectionCard } from './ConflictDetectionCard';
import { AssignmentHistoryCard } from './AssignmentHistoryCard';
import { EmployeeImpactPreview } from './EmployeeImpactPreview';
import type { CompensationRule } from '@/services/compensation/types';

interface EmployeeCompensationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedEmployeeId?: string;
}

export function EmployeeCompensationDialog({
  open,
  onOpenChange,
  selectedEmployeeId,
}: EmployeeCompensationDialogProps) {
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(selectedEmployeeId || null);
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [editingRule, setEditingRule] = useState<CompensationRule | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('rules');

  const { currencyCode } = useWorkplaceCurrency();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const { data: employees, isLoading: employeesLoading } = useCompanyEmployees();
  const { data: compensationRules, isLoading: rulesLoading } = useCompensationRules({
    profileId: selectedProfileId || '',
    activeOnly: false,
  });
  
  const { useUnassignRule } = useCompensationAssignments();
  const unassignRuleMutation = useUnassignRule();

  const selectedEmployee: any = employees?.find((emp: any) => emp.id === selectedProfileId);

  const getEmployeeInitials = (employee: any) => {
    return `${employee.first_name?.[0] || ''}${employee.last_name?.[0] || ''}`.toUpperCase();
  };

  const handleCreateRule = () => {
    setEditingRule(undefined);
    setShowRuleDialog(true);
  };

  const handleEditRule = (rule: CompensationRule) => {
    setEditingRule(rule);
    setShowRuleDialog(true);
  };

  const handleBulkAssign = (rule: CompensationRule) => {
    setEditingRule(rule);
    setShowBulkDialog(true);
  };

  const handleUnassignRule = async (rule: CompensationRule, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!selectedProfileId) return;

    try {
      await unassignRuleMutation.mutateAsync({
        ruleId: rule.id,
        profileId: selectedProfileId
      });

      toast({
        title: "Rule Unassigned",
        description: `${rule.rule_name} has been removed from ${selectedEmployee.first_name} ${selectedEmployee.last_name}`,
      });
    } catch (error) {
      console.error('Unassignment error:', error);
      toast({
        title: "Unassignment Failed",
        description: "Failed to unassign the rule. Please try again.",
        variant: "destructive",
      });
    }
  };

  const filteredEmployees = employees?.filter((employee: any) =>
    `${employee.first_name} ${employee.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    employee.email.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  if (employeesLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-7xl max-h-[95vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Employee Compensation Management
            </DialogTitle>
            <DialogDescription>
              Manage compensation rules, assignments, and analyze payroll impact for employees
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-6 h-[80vh]">
            {/* Employee List */}
            <div className="w-80 border-r pr-6">
              <div className="mb-4">
                <h3 className="text-sm font-medium text-muted-foreground mb-3">EMPLOYEES ({filteredEmployees.length})</h3>
                
                {/* Search */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search employees..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Bulk Actions */}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mb-3"
                  onClick={() => {
                    setEditingRule(undefined);
                    setShowBulkDialog(true);
                  }}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Bulk Assignment
                </Button>
              </div>
              
              <div className="space-y-2 max-h-[calc(80vh-8rem)] overflow-y-auto">
                {filteredEmployees.map((employee: any) => (
                  <Card
                    key={employee.id}
                    className={`cursor-pointer transition-colors hover:bg-accent/50 ${
                      selectedProfileId === employee.id ? 'border-primary bg-accent/20' : ''
                    }`}
                    onClick={() => setSelectedProfileId(employee.id)}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={employee.avatar_url || ''} />
                          <AvatarFallback className="text-xs">
                            {getEmployeeInitials(employee)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {employee.first_name} {employee.last_name}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {employee.email}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {employee.role || 'Employee'}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Employee Details & Tabs */}
            <div className="flex-1 min-h-0 flex flex-col">
              {selectedEmployee ? (
                <div className="flex flex-col gap-6 flex-1 min-h-0">
                  {/* Employee Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={selectedEmployee.avatar_url || ''} />
                        <AvatarFallback>
                          {getEmployeeInitials(selectedEmployee)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="text-lg font-semibold">
                          {selectedEmployee.first_name} {selectedEmployee.last_name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {selectedEmployee.email} • {selectedEmployee.role || 'Employee'}
                        </p>
                      </div>
                    </div>
                    
                    <Button size="sm" onClick={handleCreateRule}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Rule
                    </Button>
                  </div>

                  <Separator />

                  {/* Tabs for different views */}
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 min-h-0 flex flex-col gap-4">
                    <TabsList className="grid w-full grid-cols-4">
                      <TabsTrigger value="rules" className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        Rules
                        <Badge variant="secondary" className="ml-1">
                          {compensationRules?.length || 0}
                        </Badge>
                      </TabsTrigger>
                      
                      <TabsTrigger value="conflicts" className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Conflicts
                      </TabsTrigger>
                      
                      <TabsTrigger value="impact" className="flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        Impact
                      </TabsTrigger>
                      
                      <TabsTrigger value="history" className="flex items-center gap-2">
                        <History className="h-4 w-4" />
                        History
                      </TabsTrigger>
                    </TabsList>

                    {/* Rules Tab */}
                    <TabsContent value="rules" className="space-y-4 overflow-y-auto flex-1 min-h-0">
                      {compensationRules && compensationRules.length > 0 ? (
                        <div className="space-y-3">
                          {compensationRules.map((rule) => (
                            <Card key={rule.id} className="cursor-pointer hover:shadow-md transition-shadow">
                              <CardContent className="p-4" onClick={() => handleEditRule(rule)}>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium">{rule.rule_name}</p>
                                    <p className="text-sm text-muted-foreground">{rule.description}</p>
                                    <div className="flex items-center gap-2 mt-2">
                                      <Badge variant="outline" className="text-xs">
                                        {rule.rule_type.replace('_', ' ').toUpperCase()}
                                      </Badge>
                                      <span className="text-xs text-muted-foreground">
                                        {rule.calculation_method === 'fixed_amount' && formatCurrency(rule.base_rate || 0)}
                                        {rule.calculation_method === 'percentage' && `${rule.percentage_rate}%`}
                                        {rule.calculation_method === 'multiplier' && `${rule.multiplier_rate}x`}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex flex-col items-end gap-2">
                                    <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                                      {rule.is_active ? 'Active' : 'Inactive'}
                                    </Badge>
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleBulkAssign(rule);
                                        }}
                                      >
                                        <Users className="h-3 w-3 mr-1" />
                                        Bulk
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={(e) => handleUnassignRule(rule, e)}
                                        disabled={unassignRuleMutation.isPending}
                                      >
                                        <UserX className="h-3 w-3 mr-1" />
                                        Unassign
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      ) : (
                        <Card className="border-dashed">
                          <CardContent className="p-8 text-center text-muted-foreground">
                            <DollarSign className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p>No compensation rules configured</p>
                            <p className="text-sm">Add rules to manage this employee's compensation</p>
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>

                    {/* Conflicts Tab */}
                    <TabsContent value="conflicts" className="overflow-y-auto flex-1 min-h-0">
                      <ConflictDetectionCard
                        employeeRules={compensationRules || []}
                        onResolveConflict={(conflictId) => {
                          devLog('Resolving conflict:', conflictId);
                          // Implementation for resolving conflicts
                        }}
                      />
                    </TabsContent>

                    {/* Impact Tab */}
                    <TabsContent value="impact" className="overflow-y-auto flex-1 min-h-0">
                      <EmployeeImpactPreview
                        employeeId={selectedEmployee.id}
                        employeeName={`${selectedEmployee.first_name} ${selectedEmployee.last_name}`}
                        currentRules={compensationRules || []}
                        payPeriodHours={80}
                        showDetailedBreakdown={true}
                        rateContext={{
                          employeeWeeklyHours: selectedEmployee.weekly_hours,
                          fte: selectedEmployee.fte,
                        }}
                      />
                    </TabsContent>

                    {/* History Tab */}
                    <TabsContent value="history" className="overflow-y-auto flex-1 min-h-0">
                      <AssignmentHistoryCard
                        employeeId={selectedEmployee.id}
                        maxEntries={15}
                      />
                    </TabsContent>
                  </Tabs>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-center text-muted-foreground">
                  <div>
                    <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">Select an Employee</p>
                    <p className="text-sm">Choose an employee from the list to manage their compensation rules</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rule Dialog */}
      {showRuleDialog && (
        <CompensationRuleDialog
          open={showRuleDialog}
          onOpenChange={setShowRuleDialog}
          rule={editingRule}
          mode={editingRule ? 'edit' : 'create'}
          onRuleCreated={(newRule) => {
            // PHASE 3: After rule creation, show assignment dialog
            setEditingRule(newRule);
            setShowAssignmentDialog(true);
            setShowRuleDialog(false);
          }}
        />
      )}

      {/* Bulk Assignment Dialog */}
      <BulkAssignmentDialog
        open={showBulkDialog}
        onOpenChange={setShowBulkDialog}
        selectedRule={editingRule}
      />

      {/* PHASE 3: Rule Assignment Dialog */}
      <RuleAssignmentDialog
        open={showAssignmentDialog}
        onOpenChange={setShowAssignmentDialog}
        rule={editingRule || null}
      />
    </>
  );
}